import { useCallback, useRef, useState } from 'react';
import { createParser, EventSourceMessage, ParseError } from 'eventsource-parser';
import { v4 as uuidv4 } from 'uuid';
import { API_URL, ensureAccessTokenAlive, getOAuthAccessToken } from '../api';
import { cancelChatJob } from '../api/jobs';
import { StreamChatSessionType } from '@demind-inc/core';
import { useAuthContext } from '../context';
import { AssistantCommitAction, AssistantNavigation, EnrichmentPatchEvent } from '../types';
import { applyChatSSEEvent } from '../helpers/chatStreamEvents.helpers';
import {
  clearPersistedJobIfMatches,
  setPersistedJob,
} from '../helpers/jobRecovery.helpers';
import { useFeatureFlag } from '../../utils';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export const CHAT_JOB_TYPE = 'chat' as const;

/** Persisted alongside the chat job_id so a resumed poll can rebuild the
 * final-result call (handleFinalResult needs sessionType/date/sessionId/the
 * original prompt — the same arguments a live onResultOut would have had in
 * scope). */
export interface ChatJobContext {
  sessionType: StreamChatSessionType | 'general';
  sessionId?: string;
  targetDate?: string;
  prompt?: string;
}

interface SSEStreamingParams {
  sessionId?: string;
  prompt?: string;
  sessionType: StreamChatSessionType;
  targetDate?: string;
  hiddenContext?: Record<string, unknown>;
  /** v4: uncommitted suggest cards still on screen (science PendingItem). */
  pendingItems?: Array<{
    action_id: string;
    name: string;
    start_time?: string;
    end_time?: string;
    event_from?: string;
    category?: string;
  }>;
  /** Token deltas from `summary_delta` (v2/v3, and v4 when deltas are used). */
  onMessage?: (text: string, textId?: number) => void;
  /** v4 only: a complete `sequence` bubble (no per-token deltas). */
  onWholeMessage?: (message: string, textId: number) => void;
  /** v4 only: `sequence.status` scoped to a bubble's text_id. */
  onSequenceStatus?: (status: string, textId: number) => void;
  onError?: (error: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onStreamEnd?: () => void;
  onResultOut?: (result: any) => void;
  onQuestion?: (question: any) => void;
  onStatusText?: (statusText: string | null) => void;
  onEnrichmentPatch?: (patch: EnrichmentPatchEvent) => void;
  onCommitAction?: (action: AssistantCommitAction) => void;
  /** v4 only: the assistant asks the app to open a screen or modal. */
  onNavigate?: (navigation: AssistantNavigation) => void;
}

interface UseSSEStreamingReturn {
  startStreaming: (params: SSEStreamingParams) => void;
  stopStreaming: (invalidatePending?: boolean) => void;
  isStreaming: boolean;
  error: Error | null;
}

const CHAT_STREAM_PATH_V5 = '/api/v1/chat/end_to_end_streaming/v5';

export const useSSEStreaming = (): UseSSEStreamingReturn => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeJobIdRef = useRef<string | undefined>(undefined);
  const statusCallbackRef = useRef<((statusText: string | null) => void) | null>(null);
  const streamRequestSeqRef = useRef(0);
  const { user } = useAuthContext();
  const { featureFlags } = useFeatureFlag();
  const isV4Enabled = featureFlags.FF_lifestack_v4 ?? true;

  const emitStatusText = useCallback(
    (statusText: string | null) => {
      const callback = statusCallbackRef.current;
      if (callback) {
        callback(statusText);
      }
    },
    [statusCallbackRef.current]
  );

  const stopStreaming = useCallback((invalidatePending = true) => {
    const jobId = activeJobIdRef.current;
    activeJobIdRef.current = undefined;
    if (invalidatePending) {
      streamRequestSeqRef.current += 1;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    emitStatusText(null);
    statusCallbackRef.current = null;
    if (jobId) {
      clearPersistedJobIfMatches(CHAT_JOB_TYPE, jobId);
      void cancelChatJob(jobId);
    }
  }, [emitStatusText]);

  const startStreaming = useCallback(
    ({
      sessionId,
      prompt,
      sessionType,
      targetDate,
      hiddenContext,
      pendingItems,
      onMessage = () => void 0,
      onWholeMessage,
      onSequenceStatus,
      onError,
      onOpen,
      onClose,
      onStreamEnd,
      onResultOut = () => void 0,
      onQuestion,
      onStatusText,
      onEnrichmentPatch,
      onCommitAction,
      onNavigate,
    }: SSEStreamingParams) => {
      // Stop any existing stream before starting a new one
      if (isStreaming || abortControllerRef.current) {
        stopStreaming(false);
      }
      const requestSeq = ++streamRequestSeqRef.current;

      // Client-supplied idempotency key the v5 endpoint registers as a
      // recoverable job — persisted before the request is sent so a reload
      // mid-turn has something to resume from.
      const jobId = uuidv4();
      activeJobIdRef.current = jobId;
      if (jobId) {
        setPersistedJob<ChatJobContext>(CHAT_JOB_TYPE, jobId, {
          sessionType,
          sessionId,
          targetDate,
          prompt,
        });
      }

      statusCallbackRef.current = onStatusText ?? null;

      const handleEvent = (eventType: string, rawData: string, isCurrentRequest: () => boolean) => {
        if (!isCurrentRequest()) {
          return;
        }
        if (eventType === 'job') {
          // Server-confirmed job_id — reconcile in case it differs from the
          // one we generated (our record stays the source of truth for
          // polling either way since we already persisted it above).
          return;
        }
        applyChatSSEEvent(
          eventType,
          rawData,
          {
            onMessage,
            onWholeMessage,
            onSequenceStatus,
            onStatusText: emitStatusText,
            onQuestion,
            onCommitAction,
            onNavigate,
            onResultOut,
            onEnrichmentPatch,
          },
          isV4Enabled
        );
      };

      const runStream = async () => {
        const isAlive = await ensureAccessTokenAlive().catch((preflightError) => {
          console.error('Failed to verify access token before chat stream:', preflightError);
          return false;
        });
        if (requestSeq !== streamRequestSeqRef.current) {
          return;
        }
        if (!isAlive) {
          setIsStreaming(false);
          if (jobId && activeJobIdRef.current === jobId) {
            activeJobIdRef.current = undefined;
            clearPersistedJobIfMatches(CHAT_JOB_TYPE, jobId);
          }
          onError?.('Streaming error occurred');
          emitStatusText(null);
          return;
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsStreaming(true);
        setError(null);

        const isCurrentRequest = () => requestSeq === streamRequestSeqRef.current;

        const oAuthAccessToken = getOAuthAccessToken();
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          'X-Device-Type': 'web',
          'X-Time-Zone': dayjs.tz.guess(),
          'X-User-Id': user.userId,
        };
        if (oAuthAccessToken) {
          (headers as Record<string, string>).Authorization = `Bearer ${oAuthAccessToken}`;
        }

        try {
          const response = await fetch(`${API_URL}${CHAT_STREAM_PATH_V5}`, {
            method: 'POST',
            headers,
            signal: abortController.signal,
            body: JSON.stringify({
              userMessage: prompt,
              sessionId,
              userId: user.userId,
              routineId: user.routineId,
              metricId: user.metricId,
              sessionType,
              preferenceId: user.preferenceId,
              targetDate,
              hiddenContext,
              pendingItems: isV4Enabled ? pendingItems : undefined,
              timeZone: dayjs.tz.guess(),
              ...(jobId ? { job_id: jobId } : {}),
            }),
          });

          if (!isCurrentRequest()) {
            return;
          }
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          onOpen?.();

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('Response body is not readable');
          }
          const decoder = new TextDecoder();
          let finished = false;
          const releaseJob = () => {
            if (jobId && activeJobIdRef.current === jobId) {
              activeJobIdRef.current = undefined;
            }
          };
          const finishStream = () => {
            if (finished || !isCurrentRequest()) return;
            finished = true;
            onStreamEnd?.();
            setIsStreaming(false);
            releaseJob();
            if (jobId) {
              clearPersistedJobIfMatches(CHAT_JOB_TYPE, jobId);
            }
          };

          const parser = createParser({
            onEvent(event: EventSourceMessage) {
              if (event.event === 'close') {
                if (!isCurrentRequest()) return;
                setIsStreaming(false);
                onClose?.();
                emitStatusText(null);
                return;
              }
              if (event.event === 'stream_end') {
                finishStream();
                return;
              }
              handleEvent(event.event, event.data, isCurrentRequest);
            },
            onError(parseError: ParseError) {
              console.error('Chat SSE parse error:', parseError);
            },
          });

          while (!finished) {
            if (!isCurrentRequest()) {
              reader.cancel();
              return;
            }
            const { value, done } = await reader.read();
            if (done) {
              finishStream();
              return;
            }
            parser.feed(decoder.decode(value, { stream: true }));
          }
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          if (!isCurrentRequest()) {
            return;
          }
          console.error('Chat stream error:', err);
          setError(err as Error);
          setIsStreaming(false);
          if (jobId && activeJobIdRef.current === jobId) {
            activeJobIdRef.current = undefined;
          }
          onError?.('Streaming error occurred');
          emitStatusText(null);
        }
      };

      void runStream();
    },
    [emitStatusText, isStreaming, isV4Enabled, stopStreaming, user]
  );

  return {
    startStreaming,
    stopStreaming,
    isStreaming,
    error,
  };
};
